/**
 * Browser-side image downscale + re-encode used before shipping channel
 * artwork / virtual-channel artwork up to the server. Even a huge (10 MB)
 * source photo gets flattened to a ~120 KB JPEG that:
 *
 *   - sails through any ingress / Cloudflare body-size limit
 *   - stays under object-storage per-file caps
 *   - fits inside localStorage's ~5 MB thumb cache without triggering LRU
 *     eviction, so the artwork paints instantly on the next visit
 *   - loads faster on cold-cache viewers
 *
 * Guardrails:
 *   - if the browser doesn't support canvas.toBlob (very old Safari,
 *     server-side render), we return the original file untouched
 *   - never enlarges — if the source is already smaller than the target,
 *     the pixel dimensions are preserved and we only re-encode
 *   - transparent PNGs get JPEG-flattened onto a black background (matches
 *     the CRT look; the alpha channel isn't useful for a rail-card thumb)
 *
 * Returns a fresh `File` object (name preserved, `.jpg` extension) so it
 * can be dropped straight into `FormData.append("file", ...)` without any
 * consumer change.
 */

const DEFAULTS = {
  maxWidth: 960,       // 2× the largest displayed size on retina screens
  maxHeight: 720,
  quality: 0.85,       // JPEG quality — barely-visible difference vs 100%
  mimeType: "image/jpeg",
};

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function resizeImageForUpload(file, opts = {}) {
  if (!file || typeof file !== "object") return file;
  // Bail out cleanly on non-image types (shouldn't happen with
  // accept="image/*", but be safe).
  if (!file.type || !file.type.startsWith("image/")) return file;
  if (typeof HTMLCanvasElement === "undefined") return file;

  const { maxWidth, maxHeight, quality, mimeType } = { ...DEFAULTS, ...opts };

  let img;
  try {
    img = await loadImageFromFile(file);
  } catch (e) {
    // Corrupt / unsupported image — let the server reject it clearly.
    return file;
  }

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  if (!srcW || !srcH) return file;

  const scale = Math.min(1, maxWidth / srcW, maxHeight / srcH);
  const targetW = Math.round(srcW * scale);
  const targetH = Math.round(srcH * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx || !canvas.toBlob) return file;

  // Fill black to flatten any alpha channel — matches the CRT bezel
  // colour so PNGs with transparent corners don't look weird.
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const blob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), mimeType, quality)
  );
  if (!blob) return file;

  // Bail out if the "resized" version somehow ended up larger (small
  // sources already inside the target dimensions can happen). Better to
  // ship the original bytes than a same-content-larger-file re-encode.
  if (blob.size > file.size && scale === 1) return file;

  const nameNoExt = file.name.replace(/\.[^.]+$/, "");
  return new File([blob], `${nameNoExt}.jpg`, {
    type: mimeType,
    lastModified: Date.now(),
  });
}
